import axios, {
  AxiosHeaders,
  isAxiosError,
  type AxiosInstance,
  type AxiosRequestTransformer,
  type AxiosResponseTransformer,
} from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { isRecord } from '~/shared/lib/guards'
import qs from 'query-string'

// Inlined from the former `~/legacy/core` interfaces. This client is only used
// for the external Zus API (see ZusSessionProvider), which never sets a tenant,
// but the option is preserved to keep the relocation behavior-preserving.
type TenantKey = 'fireflyhealth'

axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN'
const TenantHeader = 'lucian-user-tenant'

interface RestOptions {
  url: string
  skipDecamelization?: boolean
  skipResponseCamelization?: boolean
  userTenant?: TenantKey
  getToken: () => Promise<string | null>
  log?: (message: string, params?: unknown, isError?: boolean) => void
}

/** Query parameters, which are decamelized and serialized onto the URL. */
type QueryParams = Record<string, unknown>

/**
 * A body humps can rewrite: a plain record or array. A `FormData` or `Blob` is an object to
 * `typeof` but has no enumerable keys, so humps would replace it with `{}`.
 */
const isDecamelizable = (value: unknown): value is object => {
  if (Array.isArray(value)) return true
  if (!isRecord(value)) return false
  // `getPrototypeOf` is typed `any`, so the comparison is made against a named `unknown`.
  const prototype: unknown = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

export class RestClient {
  http: AxiosInstance
  options: RestOptions

  constructor(options: RestOptions) {
    this.options = options

    // CAREFUL: any new custom header addition needs to update CORS_ALLOW_HEADERS in base.py
    const headers = new AxiosHeaders({
      'cache-control': 'no-cache',
    })

    if (options.userTenant) {
      // the back-end will interpret all requests from this client as patient requests
      headers[TenantHeader] = options.userTenant
    }

    this.http = axios.create({
      withCredentials: true,
      baseURL: options.url,
      // CAREFUL: any new custom header addition needs to update CORS_ALLOW_HEADERS in base.py
      headers,
      transformRequest: [
        (data: unknown) => {
          if (options.skipDecamelization || !isDecamelizable(data)) {
            return data
          }
          return decamelizeKeys(data, { split: /(?=[A-Z0-9])/ })
        },
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
      ],
      transformResponse: [
        ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
        (data: unknown) => {
          if (options.skipResponseCamelization || !isRecord(data)) {
            return data
          }
          return camelizeKeys(data)
        },
      ],
    })

    // Request: convert all keys to snake case and supply auth token if available.
    this.http.interceptors.request.use(async config => {
      if (isDecamelizable(config.params)) {
        config.params = decamelizeKeys(config.params)
      }

      const token = await options.getToken()
      if (token) config.headers.Authorization = `Bearer ${token}`

      return config
    })
  }

  /** Passes request activity to `options.log`; on a failure `isError` is set and `params` is the error. */
  log(message: string, params?: unknown, isError?: boolean) {
    if (this.options.log) {
      this.options.log(message, params, isError)
    }
  }

  errorHandler(url: string, error: unknown) {
    this.log(`[Err][${url}]`, error, true)
    // Rejecting with the raw axios response or request object gives the caller no stack, so the
    // rejection is an Error and the object it came from is carried on `cause`.
    if (isAxiosError(error)) {
      return Promise.reject(
        new Error(`Zus request failed: ${url}`, {
          cause: error.response ?? error.request ?? error,
        }),
      )
    }
    return Promise.reject(new Error(`Zus request failed: ${url}`, { cause: error }))
  }

  /**
   * Global GET function
   * @param {*} url | The requested endpoint
   * @param {*} params | Parameters passed to request
   */
  get<T = unknown>(url: string, params?: QueryParams): Promise<T> {
    let serialized = qs.stringify(decamelizeKeys(params ?? {}) as qs.StringifiableRecord)
    serialized = serialized.length ? `?${serialized}` : ''
    this.log(`[Req][GET][${url}${serialized}]`)
    return this.http
      .get<T>(url, { params })
      .catch(error => this.errorHandler(url, error))
      .then(({ data }) => Promise.resolve(data))
  }
  /**
   * Global POST function
   * @param {*} url | The requested endpoint
   * @param {*} params | Parameters passed to request
   */
  post<T = unknown>(url: string, params?: unknown) {
    this.log(`[Req][POST][${url}]`, params)
    return this.http
      .post<T>(url, params)
      .catch(error => this.errorHandler(url, error))
      .then(({ data }) => Promise.resolve(data))
  }

  /**
   * Global PUT function
   * @param {*} url | The requested endpoint
   * @param {*} params | Parameters passed to request
   */
  put<T = unknown>(url: string, params?: unknown) {
    this.log(`[Req][POST][${url}]`, params)
    return this.http
      .put<T>(url, params)
      .catch(error => this.errorHandler(url, error))
      .then(({ data }) => Promise.resolve(data))
  }
  /**
   * Global PATCH function
   * @param {*} url | The requested endpoint
   * @param {*} params | Parameters passed to request
   */
  patch<T = unknown>(url: string, params?: unknown) {
    this.log(`[Req][PATCH][${url}]`, params)
    return this.http
      .patch<T>(url, params)
      .catch(error => this.errorHandler(url, error))
      .then(({ data }) => Promise.resolve(data))
  }

  /**
   * Global DELETE function
   * @param {*} url | The requested endpoint
   * @param {*} params | Parameters passed to request
   */
  delete<T = unknown>(url: string) {
    this.log(`[Req][DELETE][${url}]`)
    return this.http
      .delete<T>(url)
      .catch(error => this.errorHandler(url, error))
      .then(({ data }) => Promise.resolve(data))
  }

  /**
   * Will inform Lucian back-end that this request is
   * 1) a patient request 2) in the context of the $tenantKey Tenant
   */
  setTenant(tenantKey: TenantKey) {
    const headers = this.http.defaults.headers
    // CAREFUL: any new custom header addition needs to update CORS_ALLOW_HEADERS in base.py
    headers[TenantHeader] = tenantKey
    return headers
  }
}
